import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  useClockOffMutation,
  useClockOnMutation,
  useEndBreakMutation,
  useRecordCallBackMutation,
  useRecordOnCallMutation,
  useStartBreakMutation,
  useStartTravelMutation,
} from "store/reducers/attendance";
import {
  goBackNow,
  selectClockResponse,
  selectLastClockAction,
  selectPinResponse,
} from "store/reducers/attendance/attendanceSlice";
import { useFetchLocationsQuery } from "store/reducers/location/locationAPI";

import { EarningTypes } from "./earning_types";
import { ForgotToClockOff } from "./ForgotToClockOff";
import { LocationDropdown } from "./LocationDropdown";
import { PreviousShift } from "./previous_shift";
import { ShiftActivityInsights } from "./ShiftActivityInsights";
import { Button } from "../components/button";
import { SelectDropDown } from "../components/selectDropDown";
import { Duration } from "../timesheet_viewer/table_components/duration";

// TODO: Re-enable once public site IP detection is deployed.
const enforceOnsiteRestrictions: boolean = true;

export function Clock() {
  const dispatch = useAppDispatch();
  const [earningsRate, setEarningsRate] = React.useState<any>();
  const [location, setLocation] = React.useState<LocationSchema | null>();

  const pinResponse = useAppSelector(selectPinResponse);
  const lastClockAction = useAppSelector(selectLastClockAction);
  const clockResponse = useAppSelector(selectClockResponse);
  const [clockOn, { isLoading: isLoadingClockOn }] = useClockOnMutation();
  const [startBreak, { isLoading: isLoadingStartBreak }] = useStartBreakMutation();
  const [clockOff, { isLoading: isLoadingClockOff }] = useClockOffMutation();
  const [startTravel, { isLoading: isLoadingStartTravel }] = useStartTravelMutation();
  const [endBreak, { isLoading: isLoadingEndBreak }] = useEndBreakMutation();
  const [recordOnCall, { isLoading: isLoadingRecordOnCall }] = useRecordOnCallMutation();
  const [recordCallBack, { isLoading: isLoadingRecordCallBack }] = useRecordCallBackMutation();

  const [travelToLocation, setTravelToLocation] = React.useState<LocationSchema>();
  const [startRecordingTravel, setStartRecordingTravel] = React.useState<boolean>(false);
  const { data: locations, isSuccess: isSuccessLocations } = useFetchLocationsQuery();

  const defaultLocation = React.useMemo(() => {
    if (!pinResponse || !locations) return null;
    let location = locations[0]; // Default to the first location
    if (pinResponse.guessed_location) {
      return locations.find((l) => l.id === pinResponse.guessed_location?.id) || location;
    } else if (pinResponse.current_location) {
      return locations.find((l) => l.id === pinResponse.current_location?.id) || location;
    } else if (pinResponse.last_shift?.location) {
      return locations.find((l) => l.id === pinResponse.last_shift?.location?.id) || location;
    }
    return location;
  }, [locations, pinResponse]);

  React.useEffect(() => {
    setLocation(defaultLocation);
    if (defaultLocation) {
      setTravelToLocation(defaultLocation);
    }
  }, [defaultLocation]);

  if (!pinResponse) {
    return null;
  }

  const unfinishedBreak = pinResponse.open_shift?.taken_breaks.find((b) => b.end == null);

  function onChangeEarningsType(e: any) {
    let value = e.target.value;
    setEarningsRate(value);
  }

  function onChangeLocation(e: React.ChangeEvent<HTMLSelectElement>) {
    if (!locations) throw new Error("Locations not loaded");
    if (e.target.value === "") {
      setLocation(null);
    } else {
      let workedLocation = locations.find((site) => String(site.id) === e.target.value);
      setLocation(workedLocation);
    }
  }

  let onChangeTravelToLocation: React.ComponentProps<"select">["onChange"] = function (e) {
    if (!locations) throw new Error("Locations not loaded");
    let location = locations.find((location) => location.name === e.target.value);
    setTravelToLocation(location);
  };

  // Location 7 is remote. No use warning in that case.
  const warnRemote = enforceOnsiteRestrictions && !pinResponse.ip_is_onsite && location?.id !== 7;

  if (!isSuccessLocations) return null;

  return (
    <>
      {(!clockResponse || clockResponse.type === "archive_shift") && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center mb-4">
            <span className="text-primary text-4xl font-bold mr-2">{pinResponse.employee.name}</span>{" "}
            <Button colour="danger" size="sm" onClick={() => dispatch(goBackNow())}>
              Not me (go back)
            </Button>
          </div>
          {clockResponse && <span className="text-primary text-2xl font-bold mb-2">Shift was archived.</span>}
          {warnRemote && (
            <div className="rounded bg-danger-light p-3 mb-4">
              Clock On at <span className="font-semibold">{location?.name}</span> is restricted to approved devices
              only.
            </div>
          )}
          {!pinResponse.open_shift && (
            <>
              {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
              {!locations && "Loading..."}
              {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
              {locations && (
                <div className="flex flex-col gap-3">
                  {pinResponse.employee.earnings_vocational && (
                    <EarningTypes onChangeEarningsType={onChangeEarningsType} earningsRate={earningsRate} />
                  )}
                  <LocationDropdown
                    emptyOptionLabel={null}
                    showDeleted={false}
                    showLabel="Location"
                    onChange={onChangeLocation}
                    value={location?.id ?? ""}
                  />
                  <Button
                    disabled={isLoadingClockOn || warnRemote}
                    colour="accent"
                    size="lg"
                    onClick={() =>
                      clockOn({
                        employee: pinResponse.employee,
                        location: location,
                        earningsRate: earningsRate,
                      })
                    }
                    autoFocus
                  >
                    Clock On
                  </Button>
                </div>
              )}
            </>
          )}
          {pinResponse.open_shift && pinResponse.still_valid === false && (
            <ForgotToClockOff open_shift={pinResponse.open_shift} />
          )}
          {startRecordingTravel && (
            <div className="flex flex-col mb-6">
              <SelectDropDown
                showLabel="Travel To"
                value={travelToLocation && travelToLocation.name}
                onChange={onChangeTravelToLocation}
              >
                {locations.map((location) => {
                  return <option key={location.id}>{location.name}</option>;
                })}
              </SelectDropDown>
            </div>
          )}
          {pinResponse.open_shift && pinResponse.still_valid !== false && !startRecordingTravel && !unfinishedBreak && (
            <div className="flex flex-col gap-3">
              <Button
                disabled={warnRemote || isLoadingStartBreak}
                onClick={() => pinResponse.open_shift && startBreak({ open_shift: pinResponse.open_shift })}
                colour="accent"
                autoFocus
              >
                <FontAwesomeIcon icon="coffee" /> Start Break
              </Button>
              <Button
                disabled={warnRemote || isLoadingClockOff}
                onClick={() => pinResponse.open_shift && clockOff({ open_shift: pinResponse.open_shift })}
                colour="danger"
              >
                <FontAwesomeIcon icon="power-off" /> Clock Off
              </Button>
            </div>
          )}
          {(pinResponse.open_shift || pinResponse.on_call_rostered_shift_id !== null) &&
            pinResponse.still_valid !== false &&
            !startRecordingTravel &&
            !unfinishedBreak && (
              <div className="flex flex-col gap-3">
                {pinResponse.open_shift && (
                  <Button disabled={warnRemote} onClick={() => setStartRecordingTravel(true)} colour="accent">
                    <FontAwesomeIcon icon="car-side" /> Record Travel
                  </Button>
                )}
                {pinResponse.on_call_rostered_shift_id !== null && pinResponse.on_call_event_id !== null && (
                  <Button colour="accent" disabled>
                    <FontAwesomeIcon icon="check" /> On-Call recorded
                  </Button>
                )}
                {pinResponse.on_call_rostered_shift_id !== null && pinResponse.on_call_event_id === null && (
                  <Button
                    colour="accent"
                    disabled={isLoadingRecordOnCall || isLoadingRecordCallBack}
                    onClick={() => recordOnCall({ employee_id: pinResponse.employee.id })}
                  >
                    <FontAwesomeIcon icon="user-clock" /> Record On-Call
                  </Button>
                )}
                {pinResponse.on_call_rostered_shift_id !== null && pinResponse.on_call_event_id !== null && (
                  <Button
                    colour="accent"
                    disabled={isLoadingRecordOnCall || isLoadingRecordCallBack}
                    onClick={() => recordCallBack({ employee_id: pinResponse.employee.id })}
                  >
                    <FontAwesomeIcon icon="phone" /> Record Call-Back
                  </Button>
                )}
              </div>
            )}
          {pinResponse.open_shift && pinResponse.still_valid !== false && startRecordingTravel && !unfinishedBreak && (
            <>
              <div className="flex items-center mb-2 text-warning-light">
                <FontAwesomeIcon icon="exclamation-circle" className="mr-2" />
                Only record travel if you are using your own car.
              </div>
              <div className="flex justify-between">
                <Button colour="accent" size="lg" onClick={() => setStartRecordingTravel(false)}>
                  <FontAwesomeIcon icon="arrow-left" /> Back
                </Button>
                <Button
                  colour="accent"
                  size="lg"
                  onClick={() => {
                    if (travelToLocation) {
                      startTravel({
                        employee_id: pinResponse.employee.id,
                        travel_to_location_id: travelToLocation.id,
                        travel_start_time: new Date(),
                      });
                    }
                  }}
                  className="ml-2"
                  autoFocus
                  disabled={!travelToLocation || isLoadingStartTravel}
                >
                  <FontAwesomeIcon icon="car-side" /> Start Travel
                </Button>
              </div>
            </>
          )}
          {pinResponse.open_shift && pinResponse.still_valid !== false && unfinishedBreak && (
            <Button
              colour="accent"
              disabled={isLoadingEndBreak}
              onClick={() => endBreak({ unfinishedBreak })}
              autoFocus
            >
              End Break
            </Button>
          )}
        </div>
      )}
      {clockResponse && clockResponse.type !== "archive_shift" && (
        <div className="flex flex-col gap-3">
          {lastClockAction === "start_break" && (
            <div className="text-primary text-4xl font-bold mb-4">Started Break</div>
          )}
          {clockResponse.type === "end_break" && (
            <div>
              <div className="text-primary text-4xl font-bold mb-4">Ended Break</div>
              <div className="flex flex-col bg-primary-lightest text-primary font-semibold p-4 rounded-sm mb-4">
                <span className="text-lg">Break time: </span>
                <span className="text-2xl text-accent">
                  <Duration seconds={clockResponse.break_obj.duration} showZeroHours={false} />
                </span>
              </div>
            </div>
          )}

          {clockResponse.type === "clock_off" && (
            <>
              <div className="text-primary text-4xl font-bold mb-4">Clocked off</div>
              <div className="bg-primary-lightest text-primary p-4 rounded-sm mb-4 font-semibold">
                <table className="w-full text-left border-separate border-spacing-y-2">
                  <tbody>
                    <tr>
                      <th className="pr-4 text-lg font-semibold">Shift Time</th>
                      <td className="text-2xl text-accent">
                        <Duration seconds={clockResponse.shift_obj.total_worked_duration} />
                      </td>
                    </tr>
                    {clockResponse.shift_obj.taken_breaks.length > 0 && (
                      <tr>
                        <th className="pr-4 text-lg font-semibold">Break Time</th>
                        <td className="text-2xl text-accent">
                          <Duration seconds={clockResponse.shift_obj.total_break_duration} />
                        </td>
                      </tr>
                    )}
                    {clockResponse.shift_obj.total_worked_duration !==
                      clockResponse.week_day.total_worked_ordinary_duration && (
                      <tr>
                        <th className="pr-4 text-lg font-semibold">Day Total</th>
                        <td className="text-2xl text-accent">
                          <Duration seconds={clockResponse.week_day.total_worked_ordinary_duration} />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {pinResponse.employee.radreport_user_id != null && (
                <ShiftActivityInsights shiftObj={clockResponse.shift_obj} />
              )}
            </>
          )}
          {clockResponse.type === "clock_on" && (
            <>
              <div className="text-primary text-4xl font-bold mb-4">Clocked on</div>
              <PreviousShift pinResponse={pinResponse} />
            </>
          )}
          {clockResponse.type === "start_travel" && (
            <div className="text-primary text-4xl font-bold mb-4">Travel recorded</div>
          )}
          {clockResponse.type === "record_on_call" && (
            <div className="text-primary text-4xl font-bold mb-4">On-Call recorded</div>
          )}
          {clockResponse.type === "record_call_back" && (
            <div className="text-primary text-4xl font-bold mb-4">Call-Back recorded</div>
          )}
          <Button onClick={() => dispatch(goBackNow())} colour="accent" autoFocus>
            Done, Go back
          </Button>
        </div>
      )}
    </>
  );
}
